import { useEffect, useState } from "react";
import { useToast } from "../../../../../_context/ToastContext/ToastContext";
import { useDynamicForm } from "../../../../../_components/form/hooks/useDynamicForm";
import { useMarketFormMutations } from "./useMarketFormMutations";
import { formatMarketSuccessMessage } from "../utils/marketFormRules";

/**
 * State Management Layer - mirrors _features/company/modals/
 * CompanyFormModal/hooks/useCompanyFormState.js, trimmed: no image/logo
 * state, no inline sub-modal - just the dynamic-form engine, the
 * close-confirmation dismiss guard, and submit orchestration.
 */
export function useMarketFormState(processedFormSchema, { mode, rowData, isOpen, onClose, onSuccess, refetchFilters }) {
  const { showToast } = useToast();
  const { submitMarket, isSubmitting } = useMarketFormMutations();

  const dynamicForm = useDynamicForm(processedFormSchema, { mode, rowData });
  const {
    handleSubmit: rhfHandleSubmit,
    reset,
    formState: { isDirty },
  } = dynamicForm;

  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowCloseConfirmation(false);
    }
  }, [isOpen]);

  function resetAndClose() {
    reset();
    setShowCloseConfirmation(false);
    onClose();
  }

  function attemptClose() {
    if (isDirty) {
      setShowCloseConfirmation(true);
    } else {
      resetAndClose();
    }
  }

  function cancelDiscard() {
    setShowCloseConfirmation(false);
  }

  const onSubmit = rhfHandleSubmit(async (validData) => {
    try {
      const response = await submitMarket({ mode, validData, rowData });

      showToast(formatMarketSuccessMessage(response, mode), "success");
      onSuccess?.(response);
      refetchFilters?.();
      resetAndClose();
    } catch (error) {
      console.error("Error:", error);
      showToast(`${error?.data?.message}`, "error");
    }
  });

  return {
    ...dynamicForm,
    showCloseConfirmation,
    hasUnsavedChanges: isDirty,
    isSubmitting,
    attemptClose,
    confirmDiscard: resetAndClose,
    cancelDiscard,
    onSubmit,
  };
}
